import { createAsyncTypes } from 'store/utils';

// State Key
export const KEY = 'hardware';

// Action Types
export const types = {
  PING_PRINTERS_ASYNC: createAsyncTypes(`${KEY}/PING_PRINTERS_ASYNC`),
  PING_ONE_PRINTER_ASYNC: createAsyncTypes(`${KEY}/PING_ONE_PRINTER_ASYNC`),
  PRINT_ALL_JOBS_ASYNC: createAsyncTypes(`${KEY}/PRINT_ALL_JOBS_ASYNC`),
  PRINT_ONE_JOB_ASYNC: createAsyncTypes(`${KEY}/PRINT_ONE_JOB_ASYNC`),
  CONFIGURE_PRINTER_ASYNC: createAsyncTypes(`${KEY}/CONFIGURE_PRINTER_ASYNC`),
  SCANNER_PING_ASYNC: createAsyncTypes(`${KEY}/SCANNER_PING_ASYNC`),
  SCANNER_READ_ASYNC: createAsyncTypes(`${KEY}/SCANNER_READ_ASYNC`),
  PAYMENT_PING_ASYNC: createAsyncTypes(`${KEY}/PAYMENT_PING_ASYNC`),
  EVENT_DATA_PING_ASYNC: createAsyncTypes(`${KEY}/EVENT_DATA_PING_ASYNC`),
  PAYMENT_SALE_ASYNC: createAsyncTypes(`${KEY}/PAYMENT_SALE_ASYNC`),
  PAYMENT_VOID_ASYNC: createAsyncTypes(`${KEY}/PAYMENT_VOID_ASYNC`),
  PAYMENT_CANCEL_ASYNC: createAsyncTypes(`${KEY}/PAYMENT_CANCELASYNC`),
  CAMERA_SNAP_ASYNC: createAsyncTypes(`${KEY}/CAMERA_SNAP_ASYNC`),
  REGISTER_KIOSK_PRINTERS: `${KEY}/REGISTER_KIOSK_PRINTERS`,
  SET_PRINT_DATA: `${KEY}/SET_PRINT_DATA`,
  LOG_TO_LOCAL_SERVER: `${KEY}/LOG_TO_LOCAL_SERVER`,
  RESET_PRINTER_CONFIGURED_STATUS: `${KEY}/RESET_PRINTER_CONFIGURED_STATUS`,
  RESET_SALE_DATA: `${KEY}/RESET_SALE_DATA`,
  RESET_SCANNER: `${KEY}/RESET_SCANNER`,
  RESET_CANCEL_ERROR: `${KEY}/RESET_CANCEL_ERROR`
};

export interface UserInitPayload {
  userInitiated?: boolean;
}

export interface Status {
  readonly rawCode: string;
  readonly message: string;
  readonly code: string;
}

export interface PrintJob {
  readonly message: string | null;
  readonly ticketNumber?: string;
  readonly transactionId?: string;
  /** Id of the kiosk hardware (printer) to send the print job to */
  readonly kioskHardwareId?: string;
  readonly printed?: boolean;
}

export interface PrintData {
  readonly printJobs: PrintJob[];
  readonly total: number;
  readonly count: number;
  readonly loading: boolean;
  readonly isReprint?: boolean;
}

export interface Scanner {
  readonly status: Status;
  readonly loading: boolean;
}

export interface Camera {
  readonly status: Status;
  readonly loading: boolean;
}

export interface SaleData {
  readonly amount: string;
  readonly cardNumber: string;
  readonly cardType: string;
  readonly cardholderName: string;
  readonly code: string;
  readonly currency: string;
  readonly description: string;
  readonly expiryDate: string;
  readonly orderId: string;
  readonly transactionDate: string;
  readonly voidParams?: any;
}

export interface PaymentDevice {
  readonly status: Status;
  readonly saleData: SaleData | null;
  readonly loading: boolean;
  readonly cancelLoading: boolean;
  readonly cancelSuccess: boolean;
  readonly cancelError: string | null;
  readonly voidError: string | null;
}

export interface PaymentConfig {
  readonly gateway: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly configuration: any;
}

export interface Printer {
  readonly kioskHardwareId: string;
  readonly status: Status;
  readonly loading: boolean;
  readonly configuredStatus: {
    readonly completed: boolean;
    readonly pending: boolean;
    readonly error?: string;
  };
}

export interface EventData {
  readonly status: Status;
  readonly loading: boolean;
}

export interface HardwareState {
  readonly eventData: EventData;
  readonly scanner: Scanner;
  readonly camera: Camera;
  readonly paymentDevice: PaymentDevice;
  readonly printers: Printer[];
  readonly printData: PrintData;
}

export interface RegisterKioskPrinters {
  type: typeof types.REGISTER_KIOSK_PRINTERS;
  /** Ids of the kiosk hardware where type=printer */
  payload: string[];
}

export interface ConfigurePrinterRequest {
  type: typeof types.CONFIGURE_PRINTER_ASYNC.REQUEST;
  payload: {
    kioskHardwareId: string;
  };
}

export interface ConfigurePrinterPending {
  type: typeof types.CONFIGURE_PRINTER_ASYNC.PENDING;
  payload: {
    kioskHardwareId: string;
    pending: boolean;
  };
}

export interface ConfigurePrinterSuccess {
  type: typeof types.CONFIGURE_PRINTER_ASYNC.SUCCESS;
  payload: {
    kioskHardwareId: string;
  };
}

export interface ConfigurePrinterError {
  type: typeof types.CONFIGURE_PRINTER_ASYNC.ERROR;
  payload: {
    kioskHardwareId: string;
    message: string;
  };
}

export interface ResetPrinterConfiguredStatus {
  type: typeof types.RESET_PRINTER_CONFIGURED_STATUS;
  payload: {
    kioskHardwareId: string;
  };
}

export interface PingPrintersRequest {
  type: typeof types.PING_PRINTERS_ASYNC.REQUEST;
  payload: {
    /** Include if you only want to ping a single printer */
    kioskHardwareId?: string;
    userInitiated?: boolean;
  };
}

export interface PingOnePrinterPayload {
  kioskHardwareId: string;
  userInitiated?: boolean;
}

export interface PingOnePrinterPending {
  type: typeof types.PING_ONE_PRINTER_ASYNC.PENDING;
  payload: {
    kioskHardwareId: string;
    pending: boolean;
  };
}

export interface PingOnePrinterSuccess {
  type: typeof types.PING_ONE_PRINTER_ASYNC.SUCCESS;
  payload: {
    kioskHardwareId: string;
    status: Status;
  };
}

export interface PingOnePrinterError {
  type: typeof types.PING_ONE_PRINTER_ASYNC.ERROR;
  payload: {
    kioskHardwareId: string;
    status: Status;
  };
}

export interface SetPrintData {
  type: typeof types.SET_PRINT_DATA;
  payload: {
    printJobs: PrintJob[];
    isReprint?: boolean;
  };
}

export interface PrintAllJobsRequest {
  type: typeof types.PRINT_ALL_JOBS_ASYNC.REQUEST;
}

export interface PrintAllJobsPending {
  type: typeof types.PRINT_ALL_JOBS_ASYNC.PENDING;
  payload: boolean;
}

export interface PrintOneJobSuccess {
  type: typeof types.PRINT_ONE_JOB_ASYNC.SUCCESS;
  payload: {
    kioskHardwareId: string;
    status: Status;
    jobIndex: number;
  };
}

export interface PrintOneJobError {
  type: typeof types.PRINT_ONE_JOB_ASYNC.ERROR;
  payload: {
    kioskHardwareId: string;
    status: Status;
  };
}

export interface ScannerPingRequest {
  type: typeof types.SCANNER_PING_ASYNC.REQUEST;
  payload: UserInitPayload;
}

export interface ScannerPingError {
  type: typeof types.SCANNER_PING_ASYNC.ERROR;
  payload: Status;
}

export interface ScannerPingPending {
  type: typeof types.SCANNER_PING_ASYNC.PENDING;
  payload: boolean;
}

export interface ScannerPingSuccess {
  type: typeof types.SCANNER_PING_ASYNC.SUCCESS;
  payload: Status;
}

export interface ScannerReadRequest {
  type: typeof types.SCANNER_READ_ASYNC.REQUEST;
  payload: UserInitPayload;
}

export interface ScannerReadError {
  type: typeof types.SCANNER_READ_ASYNC.ERROR;
  payload: Status;
}

export interface ScannerReadPending {
  type: typeof types.SCANNER_READ_ASYNC.PENDING;
  payload: boolean;
}

export interface ScannerReadSuccess {
  type: typeof types.SCANNER_READ_ASYNC.SUCCESS;
  payload: Status;
}

export interface ResetScanner {
  type: typeof types.RESET_SCANNER;
}

export interface PaymentPingRequest {
  type: typeof types.PAYMENT_PING_ASYNC.REQUEST;
  payload: UserInitPayload;
}

export interface PaymentPingError {
  type: typeof types.PAYMENT_PING_ASYNC.ERROR;
  payload: Status;
}

export interface PaymentPingPending {
  type: typeof types.PAYMENT_PING_ASYNC.PENDING;
  payload: boolean;
}

export interface PaymentPingSuccess {
  type: typeof types.PAYMENT_PING_ASYNC.SUCCESS;
  payload: Status;
}

export interface EventDataPingRequest {
  type: typeof types.EVENT_DATA_PING_ASYNC.REQUEST;
}

export interface EventDataPingError {
  type: typeof types.EVENT_DATA_PING_ASYNC.ERROR;
  payload: Status;
}

export interface EventDataPingPending {
  type: typeof types.EVENT_DATA_PING_ASYNC.PENDING;
  payload: boolean;
}

export interface EventDataPingSuccess {
  type: typeof types.EVENT_DATA_PING_ASYNC.SUCCESS;
  payload: Status;
}

export interface PaymentSaleRequest {
  type: typeof types.PAYMENT_SALE_ASYNC.REQUEST;
}

export interface PaymentSaleError {
  type: typeof types.PAYMENT_SALE_ASYNC.ERROR;
  payload: Status;
}

export interface PaymentSalePending {
  type: typeof types.PAYMENT_SALE_ASYNC.PENDING;
  payload: boolean;
}

export interface PaymentSaleSuccess {
  type: typeof types.PAYMENT_SALE_ASYNC.SUCCESS;
  payload: any;
}

export interface PaymentVoidRequest {
  type: typeof types.PAYMENT_VOID_ASYNC.REQUEST;
  payload: any;
}

export interface PaymentVoidError {
  type: typeof types.PAYMENT_VOID_ASYNC.ERROR;
  payload: string;
}

export interface PaymentVoidPending {
  type: typeof types.PAYMENT_VOID_ASYNC.PENDING;
  payload: boolean;
}

export interface PaymentVoidSuccess {
  type: typeof types.PAYMENT_VOID_ASYNC.SUCCESS;
}

export interface PaymentCancelRequest {
  type: typeof types.PAYMENT_CANCEL_ASYNC.REQUEST;
}

export interface PaymentCancelError {
  type: typeof types.PAYMENT_CANCEL_ASYNC.ERROR;
  payload: string;
}

export interface PaymentCancelSuccess {
  type: typeof types.PAYMENT_CANCEL_ASYNC.SUCCESS;
}

export interface PaymentCancelPending {
  type: typeof types.PAYMENT_CANCEL_ASYNC.PENDING;
  payload: boolean;
}

export interface ResetSaleData {
  type: typeof types.RESET_SALE_DATA;
}

export interface CameraSnapRequest {
  type: typeof types.CAMERA_SNAP_ASYNC.REQUEST;
  payload: Status;
}

export interface CameraSnapSuccess {
  type: typeof types.SCANNER_READ_ASYNC.Success;
  payload: Status;
}

export interface CameraSnapError {
  type: typeof types.SCANNER_READ_ASYNC.ERROR;
  payload: Status;
}

export interface CameraSnapPending {
  type: typeof types.SCANNER_READ_ASYNC.PENDING;
  payload: boolean;
}

export interface LogToLocalServer {
  type: typeof types.LOG_TO_LOCAL_SERVER;
  payload: string;
}

export interface AuthorizePaymentResponse {
  amount?: number;
  authCode?: string;
  cardholderName?: string;
  cardNumber?: string;
  cardType?: string;
  referenceNumber?: string;
  voidParams?: any;
}

export interface HardwareResponse {
  code: string;
  message: string | SaleData | AuthorizePaymentResponse;
  is_error?: boolean;
  raw_code?: any;
}

export type HardwareActionTypes =
  | ScannerPingRequest
  | ScannerPingError
  | ScannerPingPending
  | ScannerPingSuccess
  | ScannerReadRequest
  | ScannerReadError
  | ScannerReadPending
  | ScannerReadSuccess
  | PaymentSaleRequest
  | PaymentSaleError
  | PaymentSalePending
  | PaymentSaleSuccess
  | PaymentVoidRequest
  | PaymentVoidError
  | PaymentVoidPending
  | PaymentVoidSuccess
  | PaymentPingRequest
  | PaymentPingError
  | PaymentPingPending
  | PaymentPingSuccess
  | EventDataPingRequest
  | EventDataPingError
  | EventDataPingPending
  | EventDataPingSuccess
  | ConfigurePrinterRequest
  | ConfigurePrinterError
  | ConfigurePrinterPending
  | ConfigurePrinterSuccess
  | ResetSaleData
  | ResetScanner
  | PaymentCancelRequest
  | PaymentCancelSuccess
  | PaymentCancelError
  | PaymentCancelPending;
